import Vue from 'vue';
import isInViewport from '~/assets/services/DOMElement/isInViewport';
import { getPossibleImageSources, getPossibleImageExtensions } from '~/assets/services/helpers';
import imageLoading from '~/assets/services/DOMElement/imageLoading';
import { GLOBAL_VARS } from '~/config/index';

const { BREAK_POINTS } = GLOBAL_VARS;

const emit = (vnode, name, data) => {
    const handlers = (vnode.data && vnode.data.on) ||
        (vnode.componentOptions && vnode.componentOptions.listeners);

    if (handlers && handlers[name]) {
        handlers[name].fns(data);
    }
};

const errorImageSource = require('~/static/images/placeholder-error.png');

function cacheObserver(element, observer) {
    element.progressiveSrcObserver = observer;
}

function removeCacheObserver(element) {
    if (element.progressiveSrcObserver) {
        element.progressiveSrcObserver.disconnect();
        element.progressiveSrcObserver = null;
    }
}

function setBackgroundByTypeWindow(element, widthWindow) {
    const {
        TABLET: BREAK_POINT_TABLET,
        SMALL_DESKTOP: BREAK_POINT_SMALL_DESKTOP,
    } = BREAK_POINTS;

    let type = 'desktop';
    if (widthWindow <= BREAK_POINT_TABLET) {
        type = 'mobile';
    } else if (widthWindow >= BREAK_POINT_TABLET && widthWindow <= BREAK_POINT_SMALL_DESKTOP) {
        type = 'tablet';
    }

    element.style.backgroundImage = `url(${element.getAttribute(`data-${type}`)})`;
}

Vue.directive('progressiveBackground', {
    intersectionObserver: null,
    async bind(element, { value }, vnode) {
        element.progressiveBackgroundHandler = setBackgroundByTypeWindow.bind(element, element, window.innerWidth);
        const { src, handlePixelRatio = false, srcByExtension = {} } = value;
        const isMultipleBackground = typeof src !== 'string';
        const imagesSource = isMultipleBackground ? src : [src];
        let possibleImageSources = [];

        if (imagesSource) {
            possibleImageSources = await getPossibleImageSources({ imagesSource });
        }

        const img = new Image();
        const isDoublePixelRatio = handlePixelRatio && window.devicePixelRatio > 1;
        let optimalExtensionSrc = isDoublePixelRatio && possibleImageSources.length ? possibleImageSources[0].replace('.', '_x2.') : possibleImageSources;
        const [optimalExtension] = await getPossibleImageExtensions({ src: Object.values(srcByExtension)[0] });
        const optimalExtensionTag = isDoublePixelRatio && optimalExtension ? `${optimalExtension}_x2` : optimalExtension;

        if (optimalExtension && optimalExtensionTag && srcByExtension[optimalExtensionTag]) {
            optimalExtensionSrc = srcByExtension[optimalExtensionTag];
        }

        emit(vnode, 'progressiveBackgroundBefore');
        const { checkingIsInViewport, intersectionObserver } = isInViewport({ element });

        if (isMultipleBackground && imagesSource) {
            Object.keys(imagesSource).forEach((type, index) => {
                element.setAttribute(`data-${type}`, `${possibleImageSources[index]}`);
            });
            setBackgroundByTypeWindow(element, window.innerWidth);
            window.addEventListener('resize', element.progressiveBackgroundHandler);
        } else {
            checkingIsInViewport.then(() => {
                img.src = optimalExtensionSrc;
                return imageLoading(img);
            }).then(() => {
                element.style.backgroundImage = `url(${optimalExtensionSrc})`;
                emit(vnode, 'progressiveBackgroundSuccess');
            }, () => {
                element.style.backgroundImage = `url(${errorImageSource})`;
                emit(vnode, 'progressiveBackgroundError');
            }).finally(() => {
                emit(vnode, 'progressiveBackgroundChanged');
            });
        }
        cacheObserver(element, intersectionObserver);
    },
    unbind(element) {
        if (element.progressiveBackgroundHandler) {
            window.removeEventListener('resize', element.progressiveBackgroundHandler);
        }
        removeCacheObserver(element);
    },
});
